import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from '@dreamtree-org/twreact-ui';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Agent Feedback — a portal view over app_agent_feedback (the 👍/👎 users leave on
 * assistant replies, client/src/components/ChatPanel.jsx). Read-only, generic-CRUD
 * backed (list/count on AppAgentFeedback, gated `agents:view`), filterable by agent
 * and rating. Each row shows the snapshot captured at vote time — the question, the
 * answer, the model, the tools used, and any 👎 comment — so a reviewer can see what
 * was rated without opening the original session.
 */
const PAGE_SIZE = 25;
const RATING_OPTIONS = [
  { label: 'All ratings', value: '' },
  { label: '👍 Good', value: 'up' },
  { label: '👎 Bad', value: 'down' },
];

const countOf = (res) => (typeof res === 'number' ? res : (res?.count ?? res?.data ?? 0));
const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : (s || ''));

function toolsList(toolsJson) {
  try {
    const arr = JSON.parse(toolsJson || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export default function AgentFeedbackPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [agentKey, setAgentKey] = useState('');
  const [rating, setRating] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ up: 0, down: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const agentName = useMemo(() => {
    const map = {};
    agents.forEach((a) => { map[a.key] = a.name; });
    return (key) => map[key] || key;
  }, [agents]);

  useEffect(() => {
    fetchApi('agentList').then((res) => setAgents(res.agents || [])).catch(() => {});
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  // Reset to the first page whenever a filter changes.
  useEffect(() => { setPage(0); }, [agentKey, rating, applicationId]);

  const filteredRows = applicationId
    ? rows.filter(r => r.application_id == null || r.application_id === Number(applicationId))
    : rows;

  useEffect(() => {
    setLoading(true);
    setErr('');
    const where = {};
    if (agentKey) where.agent_key = agentKey;
    if (rating) where.rating = rating;
    const summaryWhere = agentKey ? { agent_key: agentKey } : {};

    Promise.all([
      fetchApi('crud', {
        model: 'AppAgentFeedback', action: 'list', where,
        orderBy: { column: 'created_at', direction: 'desc' },
        limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      }),
      fetchApi('crud', { model: 'AppAgentFeedback', action: 'count', where: { ...summaryWhere, rating: 'up' } }),
      fetchApi('crud', { model: 'AppAgentFeedback', action: 'count', where: { ...summaryWhere, rating: 'down' } }),
      // The paging total needs the SAME `where` as the list — the up/down counts above
      // are rating summaries and would give the pager the wrong number of pages.
      fetchApi('crud', { model: 'AppAgentFeedback', action: 'count', where }),
    ])
      .then(([list, up, down, matching]) => {
        setRows((Array.isArray(list) ? list : list?.data) || []);
        setCounts({ up: countOf(up), down: countOf(down) });
        setTotal(countOf(matching));
      })
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, [agentKey, rating, page]);

  const agentOptions = [
    { label: 'All agents', value: '' },
    ...agents.map((a) => ({ label: a.name, value: a.key })),
  ];

  const columns = useMemo(() => [
    {
      key: 'rating',
      label: 'Rating',
      render: (f) => (f.rating === 'up'
        ? <ThumbsUp size={16} className="text-emerald-600" fill="currentColor" />
        : <ThumbsDown size={16} className="text-red-500" fill="currentColor" />),
    },
    { key: 'agent_key', label: 'Agent', render: (f) => <span className="whitespace-nowrap font-medium">{agentName(f.agent_key)}</span> },
    { key: 'question', label: 'Question', render: (f) => <button onClick={() => nav(`/app-agent-feedback/${f.id}`)} className="block max-w-xs text-left hover:underline" title={f.question || ''}>{truncate(f.question, 120)}</button> },
    { key: 'answer', label: 'Answer', render: (f) => <span className="block max-w-sm" title={f.answer || ''}>{truncate(f.answer, 160)}</span> },
    { key: 'comment', label: 'Comment', render: (f) => <span className="block max-w-xs text-secondary-600" title={f.comment || ''}>{truncate(f.comment, 100)}</span> },
    {
      key: 'model',
      label: 'Model / tools',
      render: (f) => (
        <div className="whitespace-nowrap">
          <div className="font-mono text-xs">{f.model || '—'}</div>
          <div className="text-xs text-secondary-500">{toolsList(f.tools_json).join(', ')}</div>
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'When',
      render: (f) => <span className="whitespace-nowrap text-xs text-secondary-500">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</span>,
    },
  ], [agentName]);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Agent Feedback"
        subtitle={<>Thumbs-up / thumbs-down your users left on agent replies, with the question, answer and tools captured at the time.</>}
      />

      {/* serverSide: the toolbar's Selects drive the server `where`, so DataTable must not
          also filter client-side (it would hide rows the server already narrowed). No text
          search here — question/answer live in TEXT columns this page never queries by. */}
      <DataTable
        data={filteredRows}
        columns={columns}
        loading={loading}
        error={err}
        emptyText="No feedback yet."
        serverSide
        totalRecords={total}
        pageSize={PAGE_SIZE}
        pageNumber={page + 1}
        onPageChange={(p) => setPage(Math.max(0, p - 1))}
        toolbar={(
          <>
            <div className="w-56">
              <Select options={agentOptions} value={agentKey} onChange={setAgentKey} placeholder="All agents" searchable />
            </div>
            <div className="w-48 sm:w-56">
              <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
            </div>
            <div className="w-40">
              <Select options={RATING_OPTIONS} value={rating} onChange={setRating} placeholder="All ratings" />
            </div>
            <div className="ml-auto flex items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1 text-emerald-700"><ThumbsUp size={14} /> {counts.up}</span>
              <span className="inline-flex items-center gap-1 text-red-600"><ThumbsDown size={14} /> {counts.down}</span>
            </div>
          </>
        )}
      />
    </PageShell>
  );
}
